import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import CustomerMenu from './CustomerMenu';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Package, ClipboardList, ArrowRight, Loader2, Eye,
  RefreshCw, Search
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  pendiente_pago: 'Pendiente de pago',
  pago_confirmado: 'Pago confirmado',
  revision_archivo: 'Revisando archivo',
  aprobado_produccion: 'En producción',
  en_produccion: 'En producción',
  listo_entrega: 'Listo para entrega',
  terminado: 'Completado',
};
const STATUS_COLORS = {
  pendiente_pago: 'bg-yellow-100 text-yellow-800',
  pago_confirmado: 'bg-blue-100 text-blue-800',
  revision_archivo: 'bg-purple-100 text-purple-800',
  aprobado_produccion: 'bg-indigo-100 text-indigo-800',
  en_produccion: 'bg-orange-100 text-orange-800',
  listo_entrega: 'bg-green-100 text-green-800',
  terminado: 'bg-green-200 text-green-900',
};

const CustomerOrders = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem('kaha_session');
        const res = await axios.get(`${API}/customer/orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrders(res.data);
      } catch (err) {
        toast.error('Error al cargar pedidos');
      }
      setLoading(false);
    };
    fetchOrders();
  }, [user, authLoading]);

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <ClipboardList className="w-12 h-12 text-accent mx-auto" />
            <h2 className="text-xl font-bold text-primary">Inicia sesión</h2>
            <p className="text-muted-foreground text-sm">Para ver tus pedidos necesitas iniciar sesión con Google.</p>
            <Button onClick={() => login()} className="bg-accent text-white hover:bg-accent/90 rounded-full px-6" data-testid="orders-login-btn">
              Iniciar sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-manrope">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="logo-link">
            <Package className="w-8 h-8 text-accent" />
            <span className="text-xl font-bold text-primary">KAHA</span>
          </a>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/configurar')} className="gap-2 text-muted-foreground hover:text-primary" data-testid="orders-new-box-btn">
              <ArrowRight className="w-4 h-4" />
              <span className="hidden sm:inline">Nueva caja</span>
            </Button>
            <CustomerMenu />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary">Mis pedidos</h1>
            <p className="text-sm text-muted-foreground mt-1">{orders.length} pedido{orders.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center space-y-4">
              <ClipboardList className="w-16 h-16 text-muted-foreground/30 mx-auto" />
              <h3 className="text-lg font-semibold text-primary">Sin pedidos aun</h3>
              <p className="text-sm text-muted-foreground">Configura tu primera caja y haz tu primer pedido.</p>
              <Button onClick={() => navigate('/configurar')} className="bg-accent text-white hover:bg-accent/90 rounded-full px-6" data-testid="orders-create-btn">
                Crear mi caja
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <Card key={o.id} className="hover:border-accent/30 transition-colors" data-testid={`order-card-${o.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-bold text-primary text-base">#{o.order_number}</span>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_COLORS[o.customer_status] || 'bg-gray-100 text-gray-700'}`}>
                          {STATUS_LABELS[o.customer_status] || o.customer_status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                        <span>{o.dimensions?.length}x{o.dimensions?.width}x{o.dimensions?.height} mm</span>
                        <span>{o.material?.name || '-'}</span>
                        <span>{o.quantity} caja{o.quantity !== 1 ? 's' : ''}</span>
                        <span className="font-semibold text-primary">${o.total_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(o.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/rastreo/${o.order_number}`)}
                        className="gap-1.5"
                        data-testid={`order-track-${o.id}`}
                      >
                        <Search className="w-3.5 h-3.5" />
                        Rastrear
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/configurar?reorder=${o.id}`)}
                        className="gap-1.5 border-accent/40 text-accent hover:bg-accent/5"
                        data-testid={`order-reorder-${o.id}`}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Reordenar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CustomerOrders;
